import { FC, memo } from "react";
import { Control } from "react-hook-form";

import Grid from "@mui/material/Grid";
import List from "@mui/material/List";

import CircularProgress from "components/circular-progress";
import { STAFF_COLORS } from "features/scheduling/scheduler-side-sheet/filters/staff-filter/staff-search.component";
import type { AppointmentType } from "services/types/appointment-types.type";
import type {
  Availability,
  MemberAvailability
} from "services/types/staff-availability.type";
import type { MinimalStaffMember } from "services/types/staff.type";

import { AppointmentFormValues } from "../../appointment-form.component";
import StaffAvailabilityListItem from "../staff-availability-list-item";

const getStaffKey = (
  staffMember: MinimalStaffMember,
  availability: Availability[]
) => {
  return `${staffMember.id}_${availability
    .map(
      (availability) =>
        `${availability.startTime}_${availability.endTime}_${availability?.clinic?.id}`
    )
    .join("_")}`;
};

type StaffAvailabilityListItemsProps = {
  memberAvailability: MemberAvailability[];
  isCollapsibleView: boolean;
  control: Control<AppointmentFormValues, object>;
  setValue: (name: any, value: any, options?: any) => void;
  onStaffAvailabilityScroll: (
    event: React.UIEvent<HTMLDivElement, UIEvent>
  ) => void;
  isLoading: boolean;
  appointmentTypeById: Map<number, AppointmentType>;
};

const StaffAvailabilityListItems: FC<StaffAvailabilityListItemsProps> = memo(
  ({
    isCollapsibleView,
    memberAvailability,
    control,
    setValue,
    isLoading,
    onStaffAvailabilityScroll,
    appointmentTypeById
  }) => {
    if (isLoading) return <CircularProgress sx={{ margin: "0 auto" }} />;

    return (
      <>
        {memberAvailability?.map(({ staffMember, availability }, index) => (
          <Grid
            item
            height={isCollapsibleView ? "auto" : "100%"}
            overflow="hidden"
            xs={isCollapsibleView ? 12 : 4}
            key={getStaffKey(staffMember, availability)}
          >
            <List
              data-testid={`staff-member-availability-${staffMember.id}`}
              sx={{
                height: isCollapsibleView ? "auto" : "100%",
                overflow: isCollapsibleView ? "auto" : "hidden"
              }}
            >
              <StaffAvailabilityListItem
                control={control}
                isCollapsibleView={isCollapsibleView}
                color={STAFF_COLORS[index % STAFF_COLORS.length]}
                index={index}
                staffMember={staffMember}
                availability={availability}
                onStaffAvailabilityScroll={onStaffAvailabilityScroll}
                setValue={setValue}
                appointmentTypeById={appointmentTypeById}
              />
            </List>
          </Grid>
        ))}
      </>
    );
  }
);

export default StaffAvailabilityListItems;
